import React, { useState, useMemo } from 'react';
import {
  DollarSign,
  TrendingUp,
  CreditCard,
  Users,
  ShieldCheck,
  AlertTriangle,
  CheckCircle2,
  AlertCircle,
  Clock,
  Download,
  Search,
  Filter,
  RefreshCw,
  Layers,
  ArrowUpRight,
  ExternalLink,
  ChevronRight,
  Check,
  X,
  FileText,
  Lock,
  Unlock,
  Radio,
  SlidersHorizontal,
  Bell,
  HelpCircle,
  Building2,
  Globe,
  Tag,
  Zap,
  Info,
  Calendar,
  Sparkles,
  RotateCcw,
  Activity,
  Plus
} from 'lucide-react';
import { AppView } from '../../types';
import { PaidMonetizationMode } from '../../services/billing/billingTypes';

interface MonetizationControlCenterProps {
  onNavigateToView: (view: AppView) => void;
  onOpenSearch?: () => void;
}

export type MonetizationTab = 'monetization' | 'billing' | 'payouts' | 'tax';

export interface SubscriptionItem {
  id: string;
  organizationName: string;
  plan: 'Enterprise' | 'Pro' | 'Free' | 'Custom';
  billingCycle: 'Monthly' | 'Quarterly' | 'Annual' | 'N/A';
  nextInvoice: string;
  amountUsd: number;
  status: 'Active' | 'Past Due' | 'Canceled' | 'Trialing';
  contactEmail: string;
  provider: 'Stripe' | 'Moneroo' | 'Chariow' | 'Direct Wire';
}

export interface MonetizationChannelItem {
  id: string;
  name: string;
  description: string;
  status: 'Active' | 'Internal Test' | 'Limited Rollout' | 'Not configured' | 'Disabled';
  eligibleMarkets: string[];
  providerReadiness: {
    providerName: 'Stripe' | 'Moneroo' | 'Chariow';
    isConfigured: boolean;
    stateText: string;
  }[];
  lastReview: string;
  reviewer: string;
  revenueModel: string;
  mrrContribution: string;
}

export interface ReadinessGate {
  id: string;
  dimension:
    | 'Product'
    | 'Pricing'
    | 'Provider'
    | 'Markets'
    | 'Tax'
    | 'Legal'
    | 'Refunds'
    | 'Support'
    | 'Security'
    | 'Reconciliation'
    | 'Monitoring';
  title: string;
  description: string;
  status: 'Passed' | 'Warning' | 'Blocked';
  details: string;
  signOffBy: string;
  signOffDate: string;
}

export interface BillingEventItem {
  id: string;
  timestamp: string;
  eventName: string;
  meta: string;
  type: 'info' | 'success' | 'warning' | 'error';
  canRetry?: boolean;
}

export const MonetizationControlCenter: React.FC<MonetizationControlCenterProps> = ({
  onNavigateToView
}) => {
  // Navigation & Sub-tabs
  const [selectedDomain, setSelectedDomain] = useState<string>('revenue');
  const [activeTab, setActiveTab] = useState<MonetizationTab>('monetization');

  // Monetization Global Mode (Default: OFF per specification)
  const [monetizationMode, setMonetizationMode] = useState<PaidMonetizationMode>('OFF');
  const [pendingMode, setPendingMode] = useState<PaidMonetizationMode | null>(null);

  // Search & Filters
  const [searchOrgQuery, setSearchOrgQuery] = useState<string>('');
  const [statusFilter, setStatusFilter] = useState<string>('ALL');

  // Modals & Drawers
  const [isReadinessModalOpen, setIsReadinessModalOpen] = useState<boolean>(false);
  const [isModeChangeModalOpen, setIsModeChangeModalOpen] = useState<boolean>(false);
  const [isNewReportModalOpen, setIsNewReportModalOpen] = useState<boolean>(false);
  const [selectedSubForDetails, setSelectedSubForDetails] = useState<SubscriptionItem | null>(null);
  const [toastMessage, setToastMessage] = useState<string | null>(null);

  const triggerToast = (msg: string) => {
    setToastMessage(msg);
    setTimeout(() => setToastMessage(null), 4000);
  };

  // Real Payment Provider Configuration State
  const [providerConfig] = useState({
    stripe: {
      name: 'Stripe',
      configured: true,
      mode: 'Production & 3DS2 Enabled',
      supportedRegions: ['US', 'CA', 'EU', 'GB', 'SG']
    },
    moneroo: {
      name: 'Moneroo',
      configured: true,
      mode: 'Live (WAEMU & CEMAC Mobile Money)',
      supportedRegions: ['CI', 'SN', 'CM', 'BF', 'ML', 'BJ', 'TG']
    },
    chariow: {
      name: 'Chariow',
      configured: false,
      mode: 'Not configured (Sandbox pending)',
      supportedRegions: ['Digital Goods / Creator Marketplace']
    }
  });

  // 1. Subscription Management Dataset (Matching reference screenshot)
  const [subscriptions, setSubscriptions] = useState<SubscriptionItem[]>([
    {
      id: 'sub-001',
      organizationName: 'Acme Corp',
      plan: 'Enterprise',
      billingCycle: 'Monthly',
      nextInvoice: '2023-11-01',
      amountUsd: 2500.0,
      status: 'Active',
      contactEmail: 'billing@acmecorp.com',
      provider: 'Stripe'
    },
    {
      id: 'sub-002',
      organizationName: 'Globex Inc',
      plan: 'Pro',
      billingCycle: 'Annual',
      nextInvoice: '2024-05-15',
      amountUsd: 12000.0,
      status: 'Active',
      contactEmail: 'finance@globex.io',
      provider: 'Stripe'
    },
    {
      id: 'sub-003',
      organizationName: 'Initech',
      plan: 'Enterprise',
      billingCycle: 'Quarterly',
      nextInvoice: '2023-10-15',
      amountUsd: 7500.0,
      status: 'Past Due',
      contactEmail: 'ap@initech.corp',
      provider: 'Stripe'
    },
    {
      id: 'sub-004',
      organizationName: 'Umbrella Corp',
      plan: 'Free',
      billingCycle: 'N/A',
      nextInvoice: '-',
      amountUsd: 0.0,
      status: 'Canceled',
      contactEmail: 'admin@umbrella-pharma.com',
      provider: 'Direct Wire'
    },
    {
      id: 'sub-005',
      organizationName: 'Stark Industries',
      plan: 'Enterprise',
      billingCycle: 'Monthly',
      nextInvoice: '2023-11-05',
      amountUsd: 5000.0,
      status: 'Active',
      contactEmail: 'jarvis-billing@stark.com',
      provider: 'Stripe'
    },
    {
      id: 'sub-006',
      organizationName: 'Sovereign Pay WAEMU',
      plan: 'Enterprise',
      billingCycle: 'Monthly',
      nextInvoice: '2023-11-10',
      amountUsd: 3800.0,
      status: 'Active',
      contactEmail: 'tresorerie@sovereignpay.sn',
      provider: 'Moneroo'
    },
    {
      id: 'sub-007',
      organizationName: 'Cyberdyne Systems',
      plan: 'Enterprise',
      billingCycle: 'Annual',
      nextInvoice: '2024-01-10',
      amountUsd: 48000.0,
      status: 'Active',
      contactEmail: 'skynet-finance@cyberdyne.ai',
      provider: 'Stripe'
    }
  ]);

  // 2. Monetization Channels Dataset
  const [monetizationChannels] = useState<MonetizationChannelItem[]>([
    {
      id: 'chan-01',
      name: 'Advertising',
      description: 'Sponsor banners, brand placements & FX calculator native displays.',
      status: 'Not configured',
      eligibleMarkets: ['US', 'UK', 'FR', 'DE'],
      providerReadiness: [
        {
          providerName: 'Stripe',
          isConfigured: true,
          stateText: 'Direct Invoicing Ready'
        }
      ],
      lastReview: '2026-08-20',
      reviewer: 'Head of Growth',
      revenueModel: 'CPM / Fixed Monthly Sponsorship',
      mrrContribution: '$0 (Planned: $45,000/mo)'
    },
    {
      id: 'chan-02',
      name: 'Affiliate',
      description: 'Remittance partner commissions & commercial bank account referral bounties.',
      status: 'Internal Test',
      eligibleMarkets: ['US', 'CA', 'GB', 'EU'],
      providerReadiness: [
        {
          providerName: 'Stripe',
          isConfigured: true,
          stateText: 'Payouts Connect Ready'
        }
      ],
      lastReview: '2026-08-24',
      reviewer: 'Partnerships Lead',
      revenueModel: 'Rev-Share (0.25% per settled remittance)',
      mrrContribution: '$3,800/mo'
    },
    {
      id: 'chan-03',
      name: 'Paid Subscriptions',
      description: 'Self-serve YamooPay Pro, Individual Power User, and Small Business SaaS tiers.',
      status: 'Active',
      eligibleMarkets: ['US', 'CA', 'GB', 'FR', 'DE', 'CI', 'SN'],
      providerReadiness: [
        {
          providerName: 'Stripe',
          isConfigured: true,
          stateText: 'Card / Apple Pay / Google Pay'
        },
        {
          providerName: 'Moneroo',
          isConfigured: true,
          stateText: 'Orange Money / Wave / MTN'
        }
      ],
      lastReview: '2026-08-26',
      reviewer: 'Chief Revenue Officer',
      revenueModel: '$9.99/mo Pro | $99/yr Annual',
      mrrContribution: '$148,500/mo'
    },
    {
      id: 'chan-04',
      name: 'API Paid Billing',
      description: 'Metered developer compute units, enterprise webhooks & dedicated rate-limit tier.',
      status: 'Active',
      eligibleMarkets: ['Global'],
      providerReadiness: [
        {
          providerName: 'Stripe',
          isConfigured: true,
          stateText: 'Metered Usage Billing Engine'
        }
      ],
      lastReview: '2026-08-25',
      reviewer: 'Developer Platform Lead',
      revenueModel: '$49/mo Base + $0.0001 per extra req',
      mrrContribution: '$320,400/mo'
    },
    {
      id: 'chan-05',
      name: 'Premium Widgets',
      description: 'Embeddable white-label FX calculators & currency conversion iframes for merchant sites.',
      status: 'Limited Rollout',
      eligibleMarkets: ['FR', 'DE', 'GB', 'CI'],
      providerReadiness: [
        {
          providerName: 'Stripe',
          isConfigured: true,
          stateText: 'Subscription API'
        },
        {
          providerName: 'Chariow',
          isConfigured: false,
          stateText: 'Pending Sandbox Certification'
        }
      ],
      lastReview: '2026-08-22',
      reviewer: 'Product Director',
      revenueModel: '$29/mo per widget domain',
      mrrContribution: '$14,200/mo'
    },
    {
      id: 'chan-06',
      name: 'Enterprise Billing',
      description: 'Custom SLAs, bespoke SLA credits, dedicated treasury sweeps & consolidated invoicing.',
      status: 'Active',
      eligibleMarkets: ['Global'],
      providerReadiness: [
        {
          providerName: 'Stripe',
          isConfigured: true,
          stateText: 'ACH / SEPA Direct Debit / Custom Wire'
        }
      ],
      lastReview: '2026-08-27',
      reviewer: 'Chief Financial Officer',
      revenueModel: 'Annual Contracts ($25k - $500k ARR)',
      mrrContribution: '$550,600/mo'
    }
  ]);

  // 3. Readiness Checklist (11 Governed Dimension Gates)
  const [readinessGates, setReadinessGates] = useState<ReadinessGate[]>([
    {
      id: 'gate-01',
      dimension: 'Product',
      title: 'Product Catalog & Entitlements',
      description: 'Feature flags, subscription tier limits, and upgrade/downgrade proration matrices.',
      status: 'Passed',
      details: 'All 6 canonical products mapped to system entitlements with automated downgrade protection.',
      signOffBy: 'CPO Lead',
      signOffDate: '2026-08-26'
    },
    {
      id: 'gate-02',
      dimension: 'Pricing',
      title: 'Pricing Matrix & FX Conversion',
      description: 'Multi-currency rate pegs (USD, EUR, GBP, XOF, CAD) and decimal truncation rules.',
      status: 'Passed',
      details: 'Dynamic rate buffer set at 0.75% to absorb intraday FX volatility.',
      signOffBy: 'Head of Pricing',
      signOffDate: '2026-08-25'
    },
    {
      id: 'gate-03',
      dimension: 'Provider',
      title: 'Payment Gateway Connectivity',
      description: 'Real provider credentials and webhook signature verification for Stripe, Moneroo, Chariow.',
      status: 'Passed',
      details: 'Stripe & Moneroo fully verified in production. Chariow flagged as sandbox-only.',
      signOffBy: 'Principal Architect',
      signOffDate: '2026-08-27'
    },
    {
      id: 'gate-04',
      dimension: 'Markets',
      title: 'Jurisdiction Settlement Rules',
      description: 'Local regulatory compliance for ACH, SEPA Instant, CHAPS, and UEMOA Mobile Money.',
      status: 'Passed',
      details: 'TARGET2, Fedwire, and BCEAO settlement corridors validated.',
      signOffBy: 'Head of International',
      signOffDate: '2026-08-24'
    },
    {
      id: 'gate-05',
      dimension: 'Tax',
      title: 'Statutory Tax & VAT/TVA Engine',
      description: 'Automated VAT OSS/IOSS rules, US Sales Tax nexus calculation, and reverse charges.',
      status: 'Passed',
      details: 'Integrated with TaxJar / Avalara tax matrix v2.4.1 (Tax Region: EU & US).',
      signOffBy: 'Corporate Tax Lead',
      signOffDate: '2026-08-26'
    },
    {
      id: 'gate-06',
      dimension: 'Legal',
      title: 'Terms of Service & Auto-Renewal',
      description: 'FTC negative-option compliance, EU 14-day statutory right of withdrawal notices.',
      status: 'Passed',
      details: 'Customer checkout agreement v2026.3 cleared by Legal & Compliance.',
      signOffBy: 'General Counsel',
      signOffDate: '2026-08-23'
    },
    {
      id: 'gate-07',
      dimension: 'Refunds',
      title: 'Dispute & Refund Workflows',
      description: 'Automated credit memo creation, partial refund allocation, and dispute evidence builder.',
      status: 'Passed',
      details: 'Automated 14-day self-service refund window with anti-fraud throttling.',
      signOffBy: 'Finance Operations',
      signOffDate: '2026-08-25'
    },
    {
      id: 'gate-08',
      dimension: 'Support',
      title: 'Billing Support Escalations',
      description: 'Tier-1 and Tier-2 payment issue triage playbooks, Zendesk/Freshdesk webhook sync.',
      status: 'Passed',
      details: '24/7 Priority desk allocated for Enterprise and Pro invoice disputes.',
      signOffBy: 'VP Customer Experience',
      signOffDate: '2026-08-24'
    },
    {
      id: 'gate-09',
      dimension: 'Security',
      title: 'PCI-DSS SAQ-A & 3DS2 Challenge',
      description: 'Zero raw PAN storage, iframe client tokenization, and biometric step-up challenges.',
      status: 'Passed',
      details: 'Annual SOC-2 Type II and PCI Level 1 merchant certification active.',
      signOffBy: 'CISO Office',
      signOffDate: '2026-08-27'
    },
    {
      id: 'gate-10',
      dimension: 'Reconciliation',
      title: 'Automated Ledger Reconciliation',
      description: 'Double-entry settlement logs, gateway fee attribution, and NetSuite ERP journal sync.',
      status: 'Passed',
      details: 'Daily 03:00 UTC automated recon batch matching 99.98% of gateway transactions.',
      signOffBy: 'Corporate Controller',
      signOffDate: '2026-08-26'
    },
    {
      id: 'gate-11',
      dimension: 'Monitoring',
      title: 'Real-Time Telemetry & Alerting',
      description: 'Synthetic checkout probes, webhook failure rate alarms, and payment latency SLOs.',
      status: 'Passed',
      details: 'Datadog & PagerDuty integration active with <15s alert dispatch.',
      signOffBy: 'SRE Lead',
      signOffDate: '2026-08-27'
    }
  ]);

  // 4. Recent Billing Events Dataset (Matching Screenshot)
  const [billingEvents, setBillingEvents] = useState<BillingEventItem[]>([
    {
      id: 'evt-01',
      timestamp: '10:42:15Z',
      eventName: 'UpdateMarketConfiguration',
      meta: 'user_id: admin_4892',
      type: 'info'
    },
    {
      id: 'evt-02',
      timestamp: '09:15:02Z',
      eventName: 'PublishFinancialRuleset',
      meta: 'ruleset_v: 2.4.1 (Tax Region: EU)',
      type: 'success'
    },
    {
      id: 'evt-03',
      timestamp: '08:01:55Z',
      eventName: 'ExecutePayoutBatch_Failed',
      meta: 'gateway_timeout: str_992',
      type: 'error',
      canRetry: true
    },
    {
      id: 'evt-04',
      timestamp: '07:30:00Z',
      eventName: 'GenerateInvoices_Cron',
      meta: 'batch_id: inv_1024 (Success: 4,890)',
      type: 'info'
    },
    {
      id: 'evt-05',
      timestamp: '04:12:33Z',
      eventName: 'SyncExchangeRates',
      meta: 'source: ECB (Base: USD)',
      type: 'info'
    }
  ]);

  // Filtered Subscriptions
  const filteredSubscriptions = useMemo(() => {
    return subscriptions.filter((sub) => {
      const matchesSearch =
        !searchOrgQuery.trim() ||
        sub.organizationName.toLowerCase().includes(searchOrgQuery.toLowerCase()) ||
        sub.plan.toLowerCase().includes(searchOrgQuery.toLowerCase()) ||
        sub.contactEmail.toLowerCase().includes(searchOrgQuery.toLowerCase());

      const matchesStatus =
        statusFilter === 'ALL' || sub.status.toUpperCase() === statusFilter.toUpperCase();

      return matchesSearch && matchesStatus;
    });
  }, [subscriptions, searchOrgQuery, statusFilter]);

  // Handle Mode Change Request (Governance check)
  const handleRequestModeChange = (newMode: PaidMonetizationMode) => {
    if (newMode === monetizationMode) return;
    setPendingMode(newMode);
    setIsModeChangeModalOpen(true);
  };

  const handleConfirmModeChange = () => {
    if (!pendingMode) return;
    setMonetizationMode(pendingMode);
    setIsModeChangeModalOpen(false);

    const auditEntry: BillingEventItem = {
      id: `evt-${Date.now()}`,
      timestamp: new Date().toISOString().substring(11, 19) + 'Z',
      eventName: `SetMonetizationMode_${pendingMode}`,
      meta: `user_id: cfo_admin | previous: ${monetizationMode}`,
      type: pendingMode === 'ON' ? 'warning' : 'success'
    };

    setBillingEvents((prev) => [auditEntry, ...prev]);

    triggerToast(
      `Monetization mode switched to ${pendingMode}. Active customer servicing preserved.`
    );
  };

  // Retry Failed Event
  const handleRetryBillingEvent = (eventId: string) => {
    setBillingEvents((prev) =>
      prev.map((evt) => {
        if (evt.id === eventId) {
          return {
            ...evt,
            type: 'success',
            eventName: 'ExecutePayoutBatch_Succeeded (Retried)',
            meta: 'retry_worker: worker_04 | settled: USD 450,230',
            canRetry: false
          };
        }
        return evt;
      })
    );
    triggerToast('Payout batch re-executed successfully. Ledger updated.');
  };

  return (
    <div className="min-h-screen bg-[#F8FAFC] flex text-[#1E293B] font-sans antialiased">
      {/* =============================================================== */}
      {/* 1. LEFT NAVIGATION SIDEBAR (YamooPay CONTROL CENTER)            */}
      {/* =============================================================== */}
      <aside className="w-64 bg-white border-r border-[#E2E8F0] flex flex-col shrink-0 min-h-screen sticky top-0 h-screen overflow-y-auto">
        {/* Brand Header */}
        <div className="p-6 border-b border-[#F1F5F9]">
          <h1 className="text-xl font-black text-[#0F172A] tracking-tight leading-none">
            YamooPay
          </h1>
          <span className="text-[10px] font-bold text-[#64748B] tracking-wider uppercase block mt-1.5">
            CONTROL CENTER
          </span>
        </div>

        {/* + New Report Action Button */}
        <div className="px-5 pt-5 pb-3">
          <button
            onClick={() => setIsNewReportModalOpen(true)}
            className="w-full bg-[#0F172A] hover:bg-[#1E293B] text-white py-2.5 px-4 rounded-xl text-xs font-bold flex items-center justify-center gap-2 shadow-xs transition-all cursor-pointer"
          >
            <Plus className="w-4 h-4" />
            <span>New Report</span>
          </button>
        </div>

        {/* 7 Domains Navigation List */}
        <div className="flex-1 py-3 px-3 space-y-1">
          <button
            onClick={() => {
              setSelectedDomain('overview');
              onNavigateToView('admin-super-command-center');
            }}
            className="w-full flex items-center gap-3 px-3.5 py-2.5 rounded-xl text-xs font-medium text-[#475569] hover:bg-[#F1F5F9] hover:text-[#0F172A] transition-all cursor-pointer"
          >
            <Layers className="w-4 h-4" />
            <span>Overview</span>
          </button>

          <button
            onClick={() => {
              setSelectedDomain('customers');
              onNavigateToView('admin-organizations');
            }}
            className="w-full flex items-center gap-3 px-3.5 py-2.5 rounded-xl text-xs font-medium text-[#475569] hover:bg-[#F1F5F9] hover:text-[#0F172A] transition-all cursor-pointer"
          >
            <Users className="w-4 h-4" />
            <span>Customers & Support</span>
          </button>

          <button
            onClick={() => {
              setSelectedDomain('product');
              onNavigateToView('cpo-command-center');
            }}
            className="w-full flex items-center gap-3 px-3.5 py-2.5 rounded-xl text-xs font-medium text-[#475569] hover:bg-[#F1F5F9] hover:text-[#0F172A] transition-all cursor-pointer"
          >
            <TrendingUp className="w-4 h-4" />
            <span>Product & Growth</span>
          </button>

          {/* ACTIVE: Revenue & Finance (Mint Green Pill matching reference screenshot) */}
          <button
            onClick={() => setSelectedDomain('revenue')}
            className="w-full flex items-center gap-3 px-3.5 py-2.5 rounded-xl text-xs font-bold bg-[#D1F2E7] text-[#0A4D3C] transition-all cursor-pointer"
          >
            <DollarSign className="w-4 h-4 text-[#0A4D3C]" />
            <span>Revenue & Finance</span>
          </button>

          <button
            onClick={() => {
              setSelectedDomain('data');
              onNavigateToView('admin-ai-platform');
            }}
            className="w-full flex items-center gap-3 px-3.5 py-2.5 rounded-xl text-xs font-medium text-[#475569] hover:bg-[#F1F5F9] hover:text-[#0F172A] transition-all cursor-pointer"
          >
            <Sparkles className="w-4 h-4" />
            <span>Data & AI</span>
          </button>

          <button
            onClick={() => {
              setSelectedDomain('operations');
              onNavigateToView('admin-compliance');
            }}
            className="w-full flex items-center gap-3 px-3.5 py-2.5 rounded-xl text-xs font-medium text-[#475569] hover:bg-[#F1F5F9] hover:text-[#0F172A] transition-all cursor-pointer"
          >
            <ShieldCheck className="w-4 h-4" />
            <span>Operations & Risk</span>
          </button>

          <button
            onClick={() => {
              setSelectedDomain('configuration');
              onNavigateToView('admin-configuration');
            }}
            className="w-full flex items-center gap-3 px-3.5 py-2.5 rounded-xl text-xs font-medium text-[#475569] hover:bg-[#F1F5F9] hover:text-[#0F172A] transition-all cursor-pointer"
          >
            <SlidersHorizontal className="w-4 h-4" />
            <span>Configuration</span>
          </button>
        </div>

        {/* Sidebar Footer Links */}
        <div className="p-4 border-t border-[#F1F5F9] space-y-1">
          <button
            onClick={() => onNavigateToView('help-center')}
            className="w-full flex items-center gap-3 px-3 py-2 rounded-xl text-xs font-medium text-[#64748B] hover:text-[#0F172A] hover:bg-[#F8FAFC] transition-colors cursor-pointer"
          >
            <HelpCircle className="w-4 h-4" />
            <span>Help Center</span>
          </button>
          <button
            onClick={() => onNavigateToView('admin-roles-permissions')}
            className="w-full flex items-center gap-3 px-3 py-2 rounded-xl text-xs font-medium text-[#64748B] hover:text-[#0F172A] hover:bg-[#F8FAFC] transition-colors cursor-pointer"
          >
            <Users className="w-4 h-4" />
            <span>Account Settings</span>
          </button>
        </div>
      </aside>

      {/* =============================================================== */}
      {/* 2. MAIN CONTENT AREA                                           */}
      {/* =============================================================== */}
      <div className="flex-1 flex flex-col min-w-0">
        {/* Top Header Tab Bar (Exact match to screenshot) */}
        <header className="bg-white border-b border-[#E2E8F0] px-8 flex items-center justify-between sticky top-0 z-20">
          <div className="flex items-center gap-8">
            <button
              onClick={() => setActiveTab('monetization')}
              className={`py-4 text-xs font-bold transition-all cursor-pointer ${
                activeTab === 'monetization'
                  ? 'text-[#0F172A] border-b-2 border-[#0F172A]'
                  : 'text-[#64748B] hover:text-[#0F172A]'
              }`}
            >
              Monetization
            </button>
            <button
              onClick={() => {
                setActiveTab('billing');
                onNavigateToView('billing-command-center');
              }}
              className={`py-4 text-xs font-bold transition-all cursor-pointer ${
                activeTab === 'billing'
                  ? 'text-[#0F172A] border-b-2 border-[#0F172A]'
                  : 'text-[#64748B] hover:text-[#0F172A]'
              }`}
            >
              Billing
            </button>
            <button
              onClick={() => {
                setActiveTab('payouts');
                triggerToast('Payouts ledger loaded: 12 batches scheduled for next clearing cycle.');
              }}
              className={`py-4 text-xs font-bold transition-all cursor-pointer ${
                activeTab === 'payouts'
                  ? 'text-[#0F172A] border-b-2 border-[#0F172A]'
                  : 'text-[#64748B] hover:text-[#0F172A]'
              }`}
            >
              Payouts
            </button>
            <button
              onClick={() => {
                setActiveTab('tax');
                onNavigateToView('cfo-command-center');
              }}
              className={`py-4 text-xs font-bold transition-all cursor-pointer ${
                activeTab === 'tax'
                  ? 'text-[#0F172A] border-b-2 border-[#0F172A]'
                  : 'text-[#64748B] hover:text-[#0F172A]'
              }`}
            >
              Tax
            </button>
          </div>

          {/* Top Right Utility Icons */}
          <div className="flex items-center gap-3">
            <button
              onClick={() => triggerToast('0 unacknowledged billing alerts.')}
              className="p-2 text-[#64748B] hover:text-[#0F172A] rounded-lg hover:bg-[#F1F5F9] transition-colors cursor-pointer"
              title="Notifications"
            >
              <Bell className="w-4 h-4" />
            </button>
            <button
              onClick={() => onNavigateToView('admin-audit')}
              className="p-2 text-[#64748B] hover:text-[#0F172A] rounded-lg hover:bg-[#F1F5F9] transition-colors cursor-pointer"
              title="Audit Logs"
            >
              <Clock className="w-4 h-4" />
            </button>
            <button
              onClick={() => onNavigateToView('admin-super-command-center')}
              className="p-2 text-[#64748B] hover:text-[#0F172A] rounded-lg hover:bg-[#F1F5F9] transition-colors cursor-pointer"
              title="Command Hub"
            >
              <Layers className="w-4 h-4" />
            </button>
            <div className="w-8 h-8 rounded-full bg-[#0F172A] text-white font-bold text-xs flex items-center justify-center">
              YA
            </div>
          </div>
        </header>

        {/* Workspace Body */}
        <main className="flex-1 p-8 space-y-6 overflow-y-auto">
          {/* Breadcrumb & Action Row */}
          <div className="flex items-center justify-between flex-wrap gap-4">
            <div>
              <div className="flex items-center gap-2 text-xs text-[#64748B] mb-1">
                <span>Revenue & Finance</span>
                <span>›</span>
                <span className="text-[#0F172A] font-semibold">Monetization</span>
              </div>
              <h2 className="text-2xl font-black text-[#0F172A] tracking-tight">
                Monetization Overview
              </h2>
            </div>

            <div className="flex items-center gap-3">
              <button
                onClick={() => {
                  const exportJson = JSON.stringify(
                    {
                      timestamp: new Date().toISOString(),
                      monetizationMode,
                      metrics: {
                        arrUsd: 12450000,
                        activeSubscriptions: 4892,
                        churnRateMtd: 0.012,
                        pendingPayoutsUsd: 450230
                      },
                      monetizationChannels,
                      subscriptions,
                      readinessGates
                    },
                    null,
                    2
                  );
                  const blob = new Blob([exportJson], { type: 'application/json' });
                  const url = URL.createObjectURL(blob);
                  const a = document.createElement('a');
                  a.href = url;
                  a.download = `yamoopay-monetization-export-${new Date().toISOString().slice(0, 10)}.json`;
                  a.click();
                  triggerToast('Monetization report and subscriber dataset exported.');
                }}
                className="px-4 py-2 bg-[#E0E7FF] hover:bg-[#D4E0FC] text-[#3730A3] font-bold rounded-xl text-xs flex items-center gap-2 transition-colors cursor-pointer shadow-2xs"
              >
                <Download className="w-3.5 h-3.5" />
                <span>Export Data</span>
              </button>
            </div>
          </div>

          {/* ============================================================= */}
          {/* 3. TOP METRICS & MONETIZATION CONTROLS GRID                   */}
          {/* ============================================================= */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Left 2-Column Metrics Subgrid */}
            <div className="lg:col-span-2 grid grid-cols-1 sm:grid-cols-2 gap-6">
              {/* Card 1: ARR (Annual Recurring Revenue) */}
              <div className="bg-white border border-[#E2E8F0] rounded-2xl p-6 shadow-2xs flex flex-col justify-between">
                <div>
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-[11px] font-bold text-[#64748B] uppercase tracking-wider">
                      Annual Recurring Revenue (ARR)
                    </span>
                  </div>
                  <div className="flex items-center gap-3">
                    <span className="text-2xl font-black text-[#0F172A]">
                      USD 12,450,000
                    </span>
                    <span className="px-2 py-0.5 rounded-full text-[11px] font-bold bg-[#DCFCE7] text-[#15803D] flex items-center gap-0.5">
                      <TrendingUp className="w-3 h-3" />
                      <span>14.2%</span>
                    </span>
                  </div>
                </div>

                {/* Sparkline Graphic (Screenshot reproduction) */}
                <div className="mt-6 pt-2">
                  <svg className="w-full h-12 overflow-visible" viewBox="0 0 200 40">
                    <defs>
                      <linearGradient id="arrGradient" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="0%" stopColor="#0E7044" stopOpacity="0.25" />
                        <stop offset="100%" stopColor="#0E7044" stopOpacity="0.0" />
                      </linearGradient>
                    </defs>
                    <path
                      d="M 0,28 Q 30,32 60,22 T 120,24 T 160,12 T 200,16 L 200,40 L 0,40 Z"
                      fill="url(#arrGradient)"
                    />
                    <path
                      d="M 0,28 Q 30,32 60,22 T 120,24 T 160,12 T 200,16"
                      fill="none"
                      stroke="#0E7044"
                      strokeWidth="2"
                    />
                  </svg>
                </div>
              </div>

              {/* Card 2: Active Subscriptions Breakdown */}
              <div className="bg-white border border-[#E2E8F0] rounded-2xl p-6 shadow-2xs flex flex-col justify-between">
                <div>
                  <span className="text-[11px] font-bold text-[#64748B] uppercase tracking-wider block mb-2">
                    Active Subscriptions
                  </span>
                  <div className="text-2xl font-black text-[#0F172A] mb-4">
                    4,892
                  </div>
                </div>

                <div className="space-y-2 text-xs">
                  <div className="flex items-center justify-between text-[#475569]">
                    <div className="flex items-center gap-2">
                      <span className="w-2 h-2 rounded-full bg-[#0F172A]"></span>
                      <span>Enterprise</span>
                    </div>
                    <span className="font-bold text-[#0F172A]">1,204</span>
                  </div>
                  <div className="flex items-center justify-between text-[#475569]">
                    <div className="flex items-center gap-2">
                      <span className="w-2 h-2 rounded-full bg-[#2563EB]"></span>
                      <span>Pro</span>
                    </div>
                    <span className="font-bold text-[#0F172A]">2,450</span>
                  </div>
                  <div className="flex items-center justify-between text-[#475569]">
                    <div className="flex items-center gap-2">
                      <span className="w-2 h-2 rounded-full bg-[#94A3B8]"></span>
                      <span>Free</span>
                    </div>
                    <span className="font-bold text-[#0F172A]">1,238</span>
                  </div>
                </div>
              </div>

              {/* Card 4: Churn Rate (MTD) */}
              <div className="bg-white border border-[#E2E8F0] rounded-2xl p-6 shadow-2xs">
                <span className="text-[11px] font-bold text-[#64748B] uppercase tracking-wider block mb-2">
                  Churn Rate (MTD)
                </span>
                <div className="flex items-baseline gap-2 mb-3">
                  <span className="text-2xl font-black text-[#0F172A]">1.2%</span>
                  <span className="text-xs text-[#64748B]">Target: &lt; 2.0%</span>
                </div>

                {/* Progress bar */}
                <div className="w-full bg-[#E2E8F0] h-2 rounded-full overflow-hidden">
                  <div
                    className="bg-[#0F172A] h-full rounded-full transition-all"
                    style={{ width: '40%' }}
                  ></div>
                </div>
              </div>

              {/* Card 5: Pending Payouts */}
              <div className="bg-white border border-[#E2E8F0] rounded-2xl p-6 shadow-2xs">
                <span className="text-[11px] font-bold text-[#64748B] uppercase tracking-wider block mb-2">
                  Pending Payouts
                </span>
                <div className="text-2xl font-black text-[#0F172A] mb-3">
                  USD 450,230
                </div>
                <div className="flex items-center gap-2 text-xs text-[#64748B]">
                  <Clock className="w-3.5 h-3.5 text-[#64748B]" />
                  <span>Processing 12 batches</span>
                </div>
              </div>
            </div>

            {/* Right Card: Monetization Controls (Exact Match to Reference Screenshot) */}
            <div className="bg-white border border-[#E2E8F0] rounded-2xl p-6 shadow-2xs flex flex-col justify-between">
              <div>
                <h3 className="text-sm font-black text-[#0F172A] mb-1">
                  Monetization Controls
                </h3>
                <span className="text-[10px] font-bold text-[#64748B] uppercase tracking-wider block mb-4">
                  GLOBAL MONETIZATION MODE
                </span>

                {/* Free-First Badge when OFF */}
                {monetizationMode === 'OFF' && (
                  <div className="mb-4 p-2.5 bg-[#EFF6FF] border border-[#BFDBFE] rounded-xl flex items-center gap-2 text-xs text-[#1E40AF] font-semibold">
                    <ShieldCheck className="w-4 h-4 text-[#2563EB] shrink-0" />
                    <span>Free-first operation active</span>
                  </div>
                )}

                {/* Radio List */}
                <div className="space-y-2">
                  {/* OFF */}
                  <label
                    onClick={() => handleRequestModeChange('OFF')}
                    className={`flex items-center justify-between p-2.5 rounded-xl border transition-all cursor-pointer ${
                      monetizationMode === 'OFF'
                        ? 'bg-[#F1F5F9] border-[#CBD5E1] font-bold text-[#0F172A]'
                        : 'border-transparent hover:bg-[#F8FAFC] text-[#475569]'
                    }`}
                  >
                    <div className="flex items-center gap-3">
                      <input
                        type="radio"
                        name="monetizationMode"
                        checked={monetizationMode === 'OFF'}
                        onChange={() => {}}
                        className="accent-[#0F172A] w-4 h-4"
                      />
                      <span className="text-xs">OFF</span>
                    </div>
                  </label>

                  {/* INTERNAL_TEST */}
                  <label
                    onClick={() => handleRequestModeChange('INTERNAL_TEST')}
                    className={`flex items-center justify-between p-2.5 rounded-xl border transition-all cursor-pointer ${
                      monetizationMode === 'INTERNAL_TEST'
                        ? 'bg-[#F1F5F9] border-[#CBD5E1] font-bold text-[#0F172A]'
                        : 'border-transparent hover:bg-[#F8FAFC] text-[#475569]'
                    }`}
                  >
                    <div className="flex items-center gap-3">
                      <input
                        type="radio"
                        name="monetizationMode"
                        checked={monetizationMode === 'INTERNAL_TEST'}
                        onChange={() => {}}
                        className="accent-[#0F172A] w-4 h-4"
                      />
                      <span className="text-xs">INTERNAL_TEST</span>
                    </div>
                  </label>

                  {/* LIMITED_ROLLOUT */}
                  <label
                    onClick={() => handleRequestModeChange('LIMITED_ROLLOUT')}
                    className={`flex items-center justify-between p-2.5 rounded-xl border transition-all cursor-pointer ${
                      monetizationMode === 'LIMITED_ROLLOUT'
                        ? 'bg-[#F1F5F9] border-[#CBD5E1] font-bold text-[#0F172A]'
                        : 'border-transparent hover:bg-[#F8FAFC] text-[#475569]'
                    }`}
                  >
                    <div className="flex items-center gap-3">
                      <input
                        type="radio"
                        name="monetizationMode"
                        checked={monetizationMode === 'LIMITED_ROLLOUT'}
                        onChange={() => {}}
                        className="accent-[#0F172A] w-4 h-4"
                      />
                      <span className="text-xs">LIMITED_ROLLOUT</span>
                    </div>
                  </label>

                  {/* ON */}
                  <label
                    onClick={() => handleRequestModeChange('ON')}
                    className={`flex items-center justify-between p-2.5 rounded-xl border transition-all cursor-pointer ${
                      monetizationMode === 'ON'
                        ? 'bg-[#FEF2F2] border-[#FECACA] font-bold text-[#991B1B]'
                        : 'border-transparent hover:bg-[#F8FAFC] text-[#475569]'
                    }`}
                  >
                    <div className="flex items-center gap-3">
                      <input
                        type="radio"
                        name="monetizationMode"
                        checked={monetizationMode === 'ON'}
                        onChange={() => {}}
                        className="accent-red-600 w-4 h-4"
                      />
                      <div className="flex items-center gap-1.5 text-xs">
                        <span>ON</span>
                        <AlertTriangle className="w-3.5 h-3.5 text-amber-500" />
                      </div>
                    </div>
                  </label>
                </div>
              </div>

              {/* Critical Governance Clarification */}
              <div className="mt-4 pt-4 border-t border-[#F1F5F9] space-y-3">
                <p className="text-[11px] text-[#64748B] leading-relaxed">
                  <strong className="text-[#0F172A]">Governance Policy:</strong> New acquisition switch is separated from existing customer servicing. Historical subscriptions continue recurring billing without interruption.
                </p>

                {/* Primary Governed CTA: Review Activation Readiness (NEVER "Turn on payments") */}
                <button
                  onClick={() => setIsReadinessModalOpen(true)}
                  className="w-full py-2.5 px-4 bg-white border border-[#CBD5E1] hover:bg-[#F8FAFC] hover:border-[#94A3B8] text-[#0F172A] font-bold rounded-xl text-xs transition-all shadow-2xs cursor-pointer flex items-center justify-center gap-2"
                >
                  <ShieldCheck className="w-4 h-4 text-[#0E7044]" />
                  <span>Review Activation Readiness</span>
                </button>
              </div>
            </div>
          </div>

          {/* ============================================================= */}
          {/* 4. SUBSCRIPTION MANAGEMENT & RECENT BILLING EVENTS            */}
          {/* ============================================================= */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Left 2-Column: Subscription Management Table */}
            <div className="lg:col-span-2 bg-white border border-[#E2E8F0] rounded-2xl shadow-2xs overflow-hidden flex flex-col">
              <div className="p-6 border-b border-[#F1F5F9] flex items-center justify-between flex-wrap gap-4">
                <h3 className="text-base font-bold text-[#0F172A]">
                  Subscription Management
                </h3>

                <div className="flex items-center gap-3">
                  <div className="relative">
                    <Search className="w-3.5 h-3.5 text-[#94A3B8] absolute left-3 top-1/2 -translate-y-1/2" />
                    <input
                      type="text"
                      value={searchOrgQuery}
                      onChange={(e) => setSearchOrgQuery(e.target.value)}
                      placeholder="Search organizations..."
                      className="pl-8 pr-3 py-1.5 bg-[#F8FAFC] hover:bg-white border border-[#E2E8F0] rounded-xl text-xs text-[#0F172A] placeholder-[#94A3B8] outline-none focus:border-[#0F172A] transition-all"
                    />
                  </div>

                  <select
                    value={statusFilter}
                    onChange={(e) => setStatusFilter(e.target.value)}
                    className="px-2.5 py-1.5 bg-[#F8FAFC] border border-[#E2E8F0] rounded-xl text-xs font-semibold text-[#475569] outline-none cursor-pointer"
                  >
                    <option value="ALL">All Status</option>
                    <option value="ACTIVE">Active</option>
                    <option value="PAST DUE">Past Due</option>
                    <option value="CANCELED">Canceled</option>
                  </select>
                </div>
              </div>

              {/* Table */}
              <div className="overflow-x-auto flex-1">
                <table className="w-full text-left border-collapse text-xs">
                  <thead>
                    <tr className="border-b border-[#F1F5F9] text-[#64748B] font-semibold text-[10px] uppercase tracking-wider">
                      <th className="py-3 px-6">Organization Name</th>
                      <th className="py-3 px-4">Plan</th>
                      <th className="py-3 px-4">Billing Cycle</th>
                      <th className="py-3 px-4">Next Invoice</th>
                      <th className="py-3 px-4">Amount</th>
                      <th className="py-3 px-6 text-right">Status</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-[#F1F5F9]">
                    {filteredSubscriptions.map((sub) => (
                      <tr
                        key={sub.id}
                        onClick={() => setSelectedSubForDetails(sub)}
                        className="hover:bg-[#F8FAFC] transition-colors cursor-pointer"
                      >
                        {/* Organization Name */}
                        <td className="py-4 px-6 font-bold text-[#0F172A] whitespace-nowrap">
                          {sub.organizationName}
                        </td>

                        {/* Plan */}
                        <td className="py-4 px-4 text-[#475569] whitespace-nowrap">
                          {sub.plan}
                        </td>

                        {/* Billing Cycle */}
                        <td className="py-4 px-4 text-[#475569] whitespace-nowrap">
                          {sub.billingCycle}
                        </td>

                        {/* Next Invoice */}
                        <td className="py-4 px-4 font-mono text-[11px] text-[#64748B] whitespace-nowrap">
                          {sub.nextInvoice}
                        </td>

                        {/* Amount */}
                        <td className="py-4 px-4 font-bold text-[#0F172A] whitespace-nowrap">
                          USD {sub.amountUsd.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                        </td>

                        {/* Status (Screenshot Match: Active = Mint green pill, Past Due = Red/Rose pill, Canceled = Gray pill) */}
                        <td className="py-4 px-6 text-right whitespace-nowrap">
                          {sub.status === 'Active' && (
                            <span className="inline-block px-2.5 py-0.5 rounded text-[11px] font-semibold bg-[#DCFCE7] text-[#15803D]">
                              Active
                            </span>
                          )}
                          {sub.status === 'Past Due' && (
                            <span className="inline-block px-2.5 py-0.5 rounded text-[11px] font-semibold bg-[#FEE2E2] text-[#B91C1C]">
                              Past Due
                            </span>
                          )}
                          {sub.status === 'Canceled' && (
                            <span className="inline-block px-2.5 py-0.5 rounded text-[11px] font-medium bg-[#F1F5F9] text-[#64748B]">
                              Canceled
                            </span>
                          )}
                          {sub.status === 'Trialing' && (
                            <span className="inline-block px-2.5 py-0.5 rounded text-[11px] font-semibold bg-[#EFF6FF] text-[#2563EB]">
                              Trialing
                            </span>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>

            {/* Right 1-Column: Recent Billing Events (Exact Match to Reference Screenshot) */}
            <div className="bg-white border border-[#E2E8F0] rounded-2xl shadow-2xs p-6 flex flex-col justify-between">
              <div>
                <h3 className="text-base font-bold text-[#0F172A] mb-4">
                  Recent Billing Events
                </h3>

                <div className="space-y-4">
                  {billingEvents.map((evt) => (
                    <div key={evt.id} className="text-xs space-y-1">
                      <div className="flex items-center gap-2">
                        <span className="font-mono text-[11px] text-[#64748B] shrink-0">
                          {evt.timestamp}
                        </span>
                        <span
                          className={`font-semibold truncate ${
                            evt.type === 'error'
                              ? 'text-red-600'
                              : evt.type === 'warning'
                              ? 'text-amber-600'
                              : evt.type === 'success'
                              ? 'text-[#0E7044]'
                              : 'text-[#0F172A]'
                          }`}
                        >
                          {evt.eventName}
                        </span>
                      </div>
                      <div className="pl-16 flex items-center justify-between text-[11px] text-[#64748B]">
                        <span className="truncate">{evt.meta}</span>
                        {evt.canRetry && (
                          <button
                            onClick={() => handleRetryBillingEvent(evt.id)}
                            className="ml-2 text-xs font-bold text-red-600 hover:text-red-700 underline cursor-pointer"
                          >
                            Retry
                          </button>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              <div className="pt-4 mt-6 border-t border-[#F1F5F9] flex items-center justify-between text-xs text-[#64748B]">
                <div className="flex items-center gap-1.5">
                  <Activity className="w-3.5 h-3.5 text-emerald-600" />
                  <span>Cron dispatcher operational</span>
                </div>
                <button
                  onClick={() => {
                    const newEvt: BillingEventItem = {
                      id: `evt-${Date.now()}`,
                      timestamp: new Date().toISOString().substring(11, 19) + 'Z',
                      eventName: 'PollSettlementStatus',
                      meta: 'gateway: Stripe (All items cleared)',
                      type: 'success'
                    };
                    setBillingEvents((prev) => [newEvt, ...prev]);
                    triggerToast('Live sync completed.');
                  }}
                  className="hover:text-[#0F172A] font-semibold cursor-pointer"
                >
                  Refresh
                </button>
              </div>
            </div>
          </div>

          {/* ============================================================= */}
          {/* 5. MONETIZATION CHANNELS & PROVIDER READINESS                 */}
          {/* ============================================================= */}
          <div className="bg-white border border-[#E2E8F0] rounded-2xl shadow-2xs overflow-hidden">
            <div className="p-6 border-b border-[#F1F5F9] flex items-center justify-between flex-wrap gap-4">
              <div>
                <h3 className="text-base font-bold text-[#0F172A]">
                  Monetization Channels & Provider Readiness
                </h3>
                <p className="text-xs text-[#64748B] mt-0.5">
                  Governed revenue channels mapped against real payment providers (Stripe, Moneroo, Chariow).
                </p>
              </div>

              <div className="flex items-center gap-3">
                <div className="flex items-center gap-2 text-xs bg-[#F8FAFC] border border-[#E2E8F0] px-3 py-1.5 rounded-xl font-medium text-[#475569]">
                  <span className="w-2 h-2 rounded-full bg-emerald-500"></span>
                  <span>Stripe: Active</span>
                  <span className="text-gray-300">|</span>
                  <span className="w-2 h-2 rounded-full bg-emerald-500"></span>
                  <span>Moneroo: Active</span>
                  <span className="text-gray-300">|</span>
                  <span className="w-2 h-2 rounded-full bg-amber-400"></span>
                  <span>Chariow: Sandbox</span>
                </div>
              </div>
            </div>

            <div className="overflow-x-auto">
              <table className="w-full text-left border-collapse text-xs">
                <thead>
                  <tr className="border-b border-[#F1F5F9] text-[#64748B] font-semibold text-[10px] uppercase tracking-wider">
                    <th className="py-3.5 px-6">Channel</th>
                    <th className="py-3.5 px-4">Status</th>
                    <th className="py-3.5 px-4">Eligible Markets</th>
                    <th className="py-3.5 px-4">Provider Readiness</th>
                    <th className="py-3.5 px-4">Last Review</th>
                    <th className="py-3.5 px-6 text-right">Action</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-[#F1F5F9]">
                  {monetizationChannels.map((channel) => (
                    <tr key={channel.id} className="hover:bg-[#F8FAFC] transition-colors">
                      {/* Channel */}
                      <td className="py-4 px-6">
                        <div className="font-bold text-[#0F172A]">{channel.name}</div>
                        <div className="text-[11px] text-[#64748B] mt-0.5 line-clamp-1">
                          {channel.description}
                        </div>
                      </td>

                      {/* Status */}
                      <td className="py-4 px-4 whitespace-nowrap">
                        {channel.status === 'Active' && (
                          <span className="px-2 py-0.5 rounded text-[11px] font-semibold bg-[#DCFCE7] text-[#15803D]">
                            Active
                          </span>
                        )}
                        {channel.status === 'Internal Test' && (
                          <span className="px-2 py-0.5 rounded text-[11px] font-semibold bg-[#EFF6FF] text-[#2563EB]">
                            Internal Test
                          </span>
                        )}
                        {channel.status === 'Limited Rollout' && (
                          <span className="px-2 py-0.5 rounded text-[11px] font-semibold bg-[#FEF3C7] text-[#D97706]">
                            Limited Rollout
                          </span>
                        )}
                        {channel.status === 'Not configured' && (
                          <span className="px-2 py-0.5 rounded text-[11px] font-medium bg-[#F1F5F9] text-[#64748B]">
                            Not configured
                          </span>
                        )}
                      </td>

                      {/* Eligible Markets */}
                      <td className="py-4 px-4 whitespace-nowrap">
                        <div className="flex items-center gap-1 flex-wrap">
                          {channel.eligibleMarkets.map((mkt) => (
                            <span
                              key={mkt}
                              className="px-1.5 py-0.5 bg-[#F1F5F9] border border-[#E2E8F0] rounded text-[10px] font-mono text-[#475569]"
                            >
                              {mkt}
                            </span>
                          ))}
                        </div>
                      </td>

                      {/* Provider Readiness */}
                      <td className="py-4 px-4">
                        <div className="space-y-1">
                          {channel.providerReadiness.map((p, idx) => (
                            <div key={idx} className="flex items-center gap-1.5 text-[11px]">
                              {p.isConfigured ? (
                                <CheckCircle2 className="w-3.5 h-3.5 text-emerald-600 shrink-0" />
                              ) : (
                                <AlertTriangle className="w-3.5 h-3.5 text-amber-500 shrink-0" />
                              )}
                              <span className="font-semibold text-[#0F172A]">{p.providerName}:</span>
                              <span className="text-[#64748B]">{p.stateText}</span>
                            </div>
                          ))}
                        </div>
                      </td>

                      {/* Last Review */}
                      <td className="py-4 px-4 whitespace-nowrap">
                        <div className="font-mono text-[11px] text-[#0F172A]">{channel.lastReview}</div>
                        <div className="text-[10px] text-[#64748B]">{channel.reviewer}</div>
                      </td>

                      {/* Action */}
                      <td className="py-4 px-6 text-right whitespace-nowrap">
                        <button
                          onClick={() => {
                            setIsReadinessModalOpen(true);
                          }}
                          className="px-3 py-1.5 bg-white border border-[#CBD5E1] hover:bg-[#F8FAFC] text-[#0F172A] font-bold rounded-lg text-xs transition-all shadow-2xs cursor-pointer"
                        >
                          Review Gate
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </main>
      </div>

      {/* =============================================================== */}
      {/* MODAL 1: READINESS CHECKLIST (11 Governed Dimension Gates)      */}
      {/* =============================================================== */}
      {isReadinessModalOpen && (
        <div className="fixed inset-0 z-50 bg-black/60 backdrop-blur-xs flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl max-w-3xl w-full max-h-[90vh] shadow-2xl flex flex-col border border-[#E2E8F0] animate-in fade-in zoom-in-95 duration-150">
            {/* Modal Header */}
            <div className="p-6 border-b border-[#F1F5F9] flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl bg-[#D1F2E7] flex items-center justify-center text-[#0A4D3C]">
                  <ShieldCheck className="w-6 h-6" />
                </div>
                <div>
                  <h3 className="text-lg font-black text-[#0F172A]">
                    Monetization Readiness Checklist
                  </h3>
                  <p className="text-xs text-[#64748B] mt-0.5">
                    11-gate commercial verification before rollout changes.
                  </p>
                </div>
              </div>

              <button
                onClick={() => setIsReadinessModalOpen(false)}
                className="text-gray-400 hover:text-gray-600 p-2 rounded-lg hover:bg-gray-100 transition-colors"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            {/* Checklist Gates Body */}
            <div className="flex-1 p-6 overflow-y-auto space-y-4">
              <div className="p-4 bg-[#F8FAFC] border border-[#E2E8F0] rounded-xl flex items-center justify-between">
                <div>
                  <span className="text-xs font-bold text-[#0F172A] block">
                    Readiness Scorecard: 11 / 11 Gates Cleared (100%)
                  </span>
                  <span className="text-[11px] text-[#64748B]">
                    Current Mode: <strong className="text-[#0F172A]">{monetizationMode}</strong> (Free-first active when OFF)
                  </span>
                </div>
                <span className="px-3 py-1 bg-emerald-100 text-emerald-800 font-bold rounded-lg text-xs">
                  Ready for Controlled Rollout
                </span>
              </div>

              <div className="space-y-3">
                {readinessGates.map((gate) => (
                  <div
                    key={gate.id}
                    className="p-4 bg-white border border-[#E2E8F0] rounded-xl flex items-start justify-between gap-4 hover:border-[#CBD5E1] transition-all"
                  >
                    <div className="space-y-1">
                      <div className="flex items-center gap-2">
                        <span className="px-2 py-0.5 bg-[#F1F5F9] text-[#0F172A] rounded text-[10px] font-mono font-bold uppercase">
                          {gate.dimension}
                        </span>
                        <h4 className="text-xs font-bold text-[#0F172A]">{gate.title}</h4>
                      </div>
                      <p className="text-[11px] text-[#475569]">{gate.description}</p>
                      <p className="text-[11px] text-[#64748B] italic">"{gate.details}"</p>
                    </div>

                    <div className="text-right shrink-0">
                      <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-[11px] font-bold bg-[#DCFCE7] text-[#15803D]">
                        <Check className="w-3 h-3" />
                        <span>Passed</span>
                      </span>
                      <div className="text-[10px] text-[#64748B] mt-1 font-mono">
                        {gate.signOffBy} • {gate.signOffDate}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Modal Footer */}
            <div className="p-6 border-t border-[#F1F5F9] bg-[#F8FAFC] rounded-b-2xl flex items-center justify-between">
              <span className="text-xs text-[#64748B]">
                All gate audits are cryptographically hashed in system logs.
              </span>
              <button
                onClick={() => {
                  setIsReadinessModalOpen(false);
                  triggerToast('Readiness scorecard verified.');
                }}
                className="px-5 py-2.5 bg-[#0F172A] hover:bg-[#1E293B] text-white font-bold rounded-xl text-xs transition-colors cursor-pointer shadow-xs"
              >
                Done
              </button>
            </div>
          </div>
        </div>
      )}

      {/* =============================================================== */}
      {/* MODAL 2: MONETIZATION MODE CHANGE CONFIRMATION                  */}
      {/* =============================================================== */}
      {isModeChangeModalOpen && pendingMode && (
        <div className="fixed inset-0 z-50 bg-black/60 backdrop-blur-xs flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl max-w-lg w-full p-6 shadow-2xl border border-[#E2E8F0] space-y-5 animate-in fade-in zoom-in-95 duration-150">
            <div className="flex items-center gap-3">
              <div
                className={`w-10 h-10 rounded-xl flex items-center justify-center ${
                  pendingMode === 'ON'
                    ? 'bg-amber-100 text-amber-700'
                    : 'bg-[#D1F2E7] text-[#0A4D3C]'
                }`}
              >
                <AlertTriangle className="w-6 h-6" />
              </div>
              <div>
                <h3 className="text-base font-black text-[#0F172A]">
                  Confirm Mode Change to {pendingMode}
                </h3>
                <span className="text-xs text-[#64748B]">
                  Global Monetization Control Governance
                </span>
              </div>
            </div>

            <div className="p-4 bg-[#F8FAFC] border border-[#E2E8F0] rounded-xl text-xs space-y-2 text-[#475569]">
              <p>
                <strong>Current Mode:</strong> {monetizationMode}
              </p>
              <p>
                <strong>Target Mode:</strong> {pendingMode}
              </p>
              <div className="border-t border-[#E2E8F0] pt-2 text-[11px] text-[#64748B]">
                🛡️ <strong>Safety Guarantee:</strong> Changing acquisition mode to {pendingMode} does NOT interrupt existing customer billing or recurring renewals.
              </div>
            </div>

            <div className="flex items-center justify-end gap-3 pt-2">
              <button
                onClick={() => {
                  setPendingMode(null);
                  setIsModeChangeModalOpen(false);
                }}
                className="px-4 py-2 bg-white border border-[#CBD5E1] hover:bg-[#F8FAFC] text-[#0F172A] font-bold rounded-xl text-xs transition-colors cursor-pointer"
              >
                Cancel
              </button>
              <button
                onClick={handleConfirmModeChange}
                className="px-5 py-2 bg-[#0F172A] hover:bg-[#1E293B] text-white font-bold rounded-xl text-xs transition-colors cursor-pointer shadow-xs"
              >
                Confirm Switch
              </button>
            </div>
          </div>
        </div>
      )}

      {/* =============================================================== */}
      {/* MODAL 3: SUBSCRIPTION DETAIL DRAWER                             */}
      {/* =============================================================== */}
      {selectedSubForDetails && (
        <div className="fixed inset-0 z-50 bg-black/60 backdrop-blur-xs flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl max-w-lg w-full p-6 shadow-2xl border border-[#E2E8F0] space-y-5 animate-in fade-in zoom-in-95 duration-150">
            <div className="flex items-center justify-between border-b border-[#F1F5F9] pb-4">
              <div>
                <h3 className="text-base font-black text-[#0F172A]">
                  {selectedSubForDetails.organizationName}
                </h3>
                <span className="text-xs text-[#64748B]">{selectedSubForDetails.id}</span>
              </div>
              <button
                onClick={() => setSelectedSubForDetails(null)}
                className="text-gray-400 hover:text-gray-600 p-1.5 rounded-lg hover:bg-gray-100"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="space-y-3 text-xs">
              <div className="flex justify-between py-1.5 border-b border-[#F1F5F9]">
                <span className="text-[#64748B]">Plan Tier:</span>
                <span className="font-bold text-[#0F172A]">{selectedSubForDetails.plan}</span>
              </div>
              <div className="flex justify-between py-1.5 border-b border-[#F1F5F9]">
                <span className="text-[#64748B]">Billing Cadence:</span>
                <span className="font-bold text-[#0F172A]">{selectedSubForDetails.billingCycle}</span>
              </div>
              <div className="flex justify-between py-1.5 border-b border-[#F1F5F9]">
                <span className="text-[#64748B]">Recurring Amount:</span>
                <span className="font-bold text-[#0F172A]">
                  USD {selectedSubForDetails.amountUsd.toFixed(2)}
                </span>
              </div>
              <div className="flex justify-between py-1.5 border-b border-[#F1F5F9]">
                <span className="text-[#64748B]">Next Scheduled Invoice:</span>
                <span className="font-mono text-[#0F172A]">{selectedSubForDetails.nextInvoice}</span>
              </div>
              <div className="flex justify-between py-1.5 border-b border-[#F1F5F9]">
                <span className="text-[#64748B]">Settlement Gateway:</span>
                <span className="font-bold text-[#0F172A]">{selectedSubForDetails.provider}</span>
              </div>
              <div className="flex justify-between py-1.5 border-b border-[#F1F5F9]">
                <span className="text-[#64748B]">Billing Contact:</span>
                <span className="font-mono text-[#0F172A]">{selectedSubForDetails.contactEmail}</span>
              </div>
            </div>

            <div className="flex items-center justify-end gap-3 pt-2">
              <button
                onClick={() => {
                  triggerToast(`Generated payment receipt for ${selectedSubForDetails.organizationName}`);
                  setSelectedSubForDetails(null);
                }}
                className="px-4 py-2 bg-[#F1F5F9] hover:bg-[#E2E8F0] text-[#0F172A] font-bold rounded-xl text-xs transition-colors cursor-pointer"
              >
                Download Invoice
              </button>
              <button
                onClick={() => setSelectedSubForDetails(null)}
                className="px-5 py-2 bg-[#0F172A] hover:bg-[#1E293B] text-white font-bold rounded-xl text-xs transition-colors cursor-pointer"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* =============================================================== */}
      {/* MODAL 4: NEW REPORT MODAL                                       */}
      {/* =============================================================== */}
      {isNewReportModalOpen && (
        <div className="fixed inset-0 z-50 bg-black/60 backdrop-blur-xs flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl max-w-md w-full p-6 shadow-2xl border border-[#E2E8F0] space-y-4 animate-in fade-in zoom-in-95 duration-150">
            <div className="flex items-center justify-between">
              <h3 className="text-base font-black text-[#0F172A]">Generate Financial Report</h3>
              <button
                onClick={() => setIsNewReportModalOpen(false)}
                className="text-gray-400 hover:text-gray-600"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="space-y-3 text-xs">
              <label className="block text-[#475569] font-medium">Report Type</label>
              <select className="w-full p-2.5 bg-[#F8FAFC] border border-[#E2E8F0] rounded-xl text-xs font-semibold text-[#0F172A] outline-none">
                <option>ARR & Subscriber Cohort Analysis (Q3 2026)</option>
                <option>Gateway Settlement Reconciliation & Fee Attribution</option>
                <option>Global Tax VAT/TVA Ledger Summary</option>
                <option>Churn & Downgrade Prevention Audit</option>
              </select>

              <label className="block text-[#475569] font-medium mt-2">Export Format</label>
              <div className="flex items-center gap-4">
                <label className="flex items-center gap-2">
                  <input type="radio" name="exportFmt" defaultChecked className="accent-[#0F172A]" />
                  <span>PDF Executive Summary</span>
                </label>
                <label className="flex items-center gap-2">
                  <input type="radio" name="exportFmt" className="accent-[#0F172A]" />
                  <span>CSV / Excel Ledger</span>
                </label>
              </div>
            </div>

            <div className="flex items-center justify-end gap-3 pt-3 border-t border-[#F1F5F9]">
              <button
                onClick={() => setIsNewReportModalOpen(false)}
                className="px-4 py-2 text-xs font-semibold text-[#64748B] hover:text-[#0F172A]"
              >
                Cancel
              </button>
              <button
                onClick={() => {
                  setIsNewReportModalOpen(false);
                  triggerToast('Report queued in background worker. Export will be ready in 15 seconds.');
                }}
                className="px-5 py-2 bg-[#0F172A] hover:bg-[#1E293B] text-white font-bold rounded-xl text-xs shadow-xs"
              >
                Generate
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Toast Feedback */}
      {toastMessage && (
        <div className="fixed bottom-6 right-6 z-50 bg-[#0F172A] text-white text-xs font-bold px-4 py-3 rounded-xl shadow-2xl flex items-center gap-2 animate-in slide-in-from-bottom-5 duration-200">
          <CheckCircle2 className="w-4 h-4 text-emerald-400 shrink-0" />
          <span>{toastMessage}</span>
        </div>
      )}
    </div>
  );
};
